import { useState } from 'react';
import { uniqueId } from 'lodash';
import { skipToken, useSuspenseQuery } from '@apollo/client/react';

import {
  DepositAddIntegrationLayout,
  CustomAssetFormTable,
  type ICustomAssetTableRow,
} from 'src/components/Deposit/Add';
import { AssetTypeEnum, CustomIntegrationProviderEnum } from 'src/graphql/schema';
import { AssetsDepositSourceDocument } from 'src/graphql/actions/asset/assetsDepositSource.query';
import { useAddDeposit } from 'src/context/deposit';
import { withSuspense } from 'src/hoc';

type IAssetFormTableProps = {
  provider: CustomIntegrationProviderEnum;
  dataSource: ICustomAssetTableRow[];
  onAdd: (item: { title?: string; url: string }) => void;
  onRemove: (key: string) => void;
};

const AssetFormTable = withSuspense(({ provider, dataSource, onAdd, onRemove }: IAssetFormTableProps) => {
  const {
    ui: { selectedEscrowId },
  } = useAddDeposit();

  // Fails open: errorPolicy 'all' returns the error instead of throwing, so only the staged list is checked
  const { data: existingAssetsData } = useSuspenseQuery(
    AssetsDepositSourceDocument,
    selectedEscrowId
      ? {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all',
          variables: {
            softwareEscrowId: selectedEscrowId,
            type: AssetTypeEnum.CustomRepo,
            perPage: 100,
          },
        }
      : skipToken,
  );

  const existingDepositSources = new Set(
    existingAssetsData?.assetsSource.nodes.map(({ depositSource }) => depositSource?.trim()),
  );

  const validateUniqueAsset = async ({ url }: { url: string }, currentItems: ICustomAssetTableRow[]) => {
    const trimmedUrl = url.trim();
    // depositSource stores only the last path segment of the URL (repository / bucket name)
    const depositSource = trimmedUrl.split('/').filter(Boolean).pop();
    const isStaged = currentItems.some((item) => item.url.trim() === trimmedUrl);

    if (isStaged || existingDepositSources.has(depositSource)) throw new Error('Asset already added');
  };

  return (
    <CustomAssetFormTable
      provider={provider}
      dataSource={dataSource}
      onAdd={onAdd}
      onRemove={onRemove}
      customValidator={validateUniqueAsset}
    />
  );
});

const CustomDepositContainer = () => {
  const {
    ui: { integration, customData, setCustomData, getBack, history },
  } = useAddDeposit();

  const [dataSource, setDataSource] = useState<ICustomAssetTableRow[]>(
    customData.map((item) => ({
      id: uniqueId(),
      title: item.title || '',
      url: item.url,
      isEditable: false,
    })),
  );

  const provider = integration?.platform as CustomIntegrationProviderEnum;

  const onAdd = ({ title, url }: { title?: string; url: string }) =>
    setDataSource((prev) => [...prev, { title: title || '', url, id: uniqueId() }]);

  const onRemove = (key: string) => setDataSource((prev) => prev.filter((item) => item.id !== key));

  const onConfirm = () => {
    const assetsInput = dataSource.map(({ title, url }) => ({
      url,
      ...(title && { title }),
    }));
    setCustomData(assetsInput);
  };

  return (
    <DepositAddIntegrationLayout
      integration={{
        provider: integration?.platform || '',
        account: integration?.name || '',
      }}
      onConfirm={onConfirm}
      confirmButton={{
        disabled: !dataSource.length,
      }}
      onBack={getBack}
      isBackButtonVisible={history.length > 1}
    >
      <div className="px-6">
        <div className="pb-6 space-y-1.5">
          <h4 className="text-lg font-bold text-foreground">Asset Details</h4>
          <p className="text-sm text-muted-foreground">Add the details of the asset you want to deposit into escrow.</p>
        </div>
        <AssetFormTable provider={provider} dataSource={dataSource} onAdd={onAdd} onRemove={onRemove} />
      </div>
    </DepositAddIntegrationLayout>
  );
};

export default CustomDepositContainer;
